import styled, { css } from "styled-components";
import { ReactNode, useEffect, useState } from "react";
import * as React from "react";
import { DURATION } from "../Styles/GlobalStyles.ts";
import { Gap, HStack, SPACE_PREFIX } from "./Stacks.ts";
import { BiggieSmall } from "./Typography.ts";

const Description = styled.small`
    color: var(--disabled-text);
`;

type ButtonProps = {
    fill_width?: boolean;
    ref?: React.Ref<any>;
    icon?: ReactNode;
    type?: "submit";
    variant?: "primary" | "default";
    size?: "slim" | "mini" | "default";
    danger?: boolean;
    disabled?: boolean;
    is_pressed?: boolean;
    children?: ReactNode;
    onClick?: () => void | Promise<void>;
    id?: string;
    align?: "start" | "center";
    padding_vertical?: Gap;
    padding_horizontal?: Gap;
    description?: string;
};

const IconContainer = styled.div`
    display: flex;
    align-items: center;
`;

const Centered = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

type Text = {
    align: "start" | "center";
    not_full_width?: boolean;
};
const Text = styled.p<Text>`
    display: inline-block;
    ${(props) =>
        !props.not_full_width &&
        css`
            width: 100%;
        `};
    text-align: ${(props) => (props.align === "start" ? "start" : "center")};
`;

const default_button_disabled = css`
    --bg-color: var(--background);
    --text-color: var(--disabled-text);
    --outline-color: var(--disabled-outline);
    --shadow-color: transparent;
`;

const default_button_danger = css`
    --bg-color: var(--background);
    --text-color: var(--danger);
    --outline-color: var(--danger);
    --shadow-color: var(--danger-shadow);
`;

const default_button = css`
    --bg-color: var(--background);
    --text-color: var(--text);
    --outline-color: var(--text);
    --shadow-color: var(--accent-shadow);
`;

const primary_button_disabled = css`
    --bg-color: var(--disabled);
    --text-color: var(--disabled-text);
    --outline-color: transparent;
    --shadow-color: transparent;
`;

const primary_button_danger = css`
    --bg-color: var(--danger);
    --text-color: var(--danger-text);
    --outline-color: transparent;
    --shadow-color: var(--danger-shadow);
`;

const primary_button = css`
    --bg-color: var(--accent);
    --text-color: var(--accent-text);
    --outline-color: transparent;
    --shadow-color: var(--accent-shadow);
`;

export const button_pressed_effect = css`
    transform: translate(0px, 0px);
    box-shadow: none;

    &:before {
        box-shadow: inset 5px 5px var(--shadow-color);
    }
`;

export const ButtonBase = styled.button<ButtonProps>`
    ${(props) =>
        props.variant === "primary" &&
        (props.disabled ? primary_button_disabled : props.danger ? primary_button_danger : primary_button)};

    ${(props) =>
        props.variant === "default" &&
        (props.disabled ? default_button_disabled : props.danger ? default_button_danger : default_button)};

    --duration: ${DURATION}ms;

    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    ${(props) =>
        props.fill_width &&
        css`
            width: 100%;
        `} //width: 100%;

        //display: inline-block;

    ${(props) =>
        props.padding_vertical
            ? css`
                  padding-block: var(${SPACE_PREFIX + props.padding_vertical});
              `
            : props.size === "mini"
              ? css`
                    padding-block: var(--spacing-6);
                `
              : props.size === "slim"
                ? css`
                      padding-block: 2px;
                  `
                : css`
                      padding-block: 10px;
                  `}
    ${(props) =>
        props.padding_horizontal
            ? css`
                  padding-inline: var(${SPACE_PREFIX + props.padding_horizontal});
              `
            : props.size === "mini"
              ? css`
                    padding-inline: 18px;
                `
              : props.size === "slim"
                ? css`
                      padding-inline: var(--spacing-12);
                  `
                : css`
                      padding-inline: 20px;
                  `}


    outline: none;
    border: none;

    gap: var(--spacing-6);

    background-color: var(--bg-color);
    color: var(--text-color);
    text-decoration: none;
    transition: transform, box-shadow, border-radius;
    transition-duration: var(--duration);
    transition-timing-function: ease-in-out;

    ${(props) =>
        props.size === "mini"
            ? css`
                  border-radius: var(--border-radius-small);
              `
            : css`
                  border-radius: var(--border-radius);
              `}
    &:before {
        content: "";
        ${(props) =>
            props.variant === "default" &&
            css`
                border: var(--border-width) solid var(--outline-color);
            `}
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        ${(props) =>
            props.size === "mini"
                ? css`
                      border-radius: var(--border-radius-small);
                  `
                : css`
                      border-radius: var(--border-radius);
                  `}
        transition: box-shadow var(--duration) ease-in-out, border-radius var(--duration) ease-in-out;
    }

    ${(props) => !props.disabled && props.is_pressed && button_pressed_effect}

    ${(props) =>
        !props.disabled
            ? css`
                  ${!props.is_pressed &&
                  css`
                      &:hover,
                      &:focus-visible {
                          transform: translate(-5px, -5px);
                          box-shadow: 6px 6px 0 -1px var(--shadow-color);
                      }
                  `}
                  &:active {
                      ${button_pressed_effect}
                  }
              `
            : css`
                  cursor: not-allowed;
              `}
`;

export const Button: React.FC<ButtonProps> = ({
    description,
    fill_width,
    ref,
    icon,
    type,
    variant = "default",
    danger = false,
    disabled = false,
    onClick,
    children,
    id,
    align = "center",
    padding_vertical,
    padding_horizontal,
    size = "default",
}) => {
    const timeoutRef = React.useRef();
    const [isPressed, setIsPressed] = useState(false);

    const handleClick = () => {
        setIsPressed(true);
        timeoutRef.current = setTimeout(() => {
            setIsPressed(false);
        }, DURATION);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.target.classList.add("active");
        }
    };

    useEffect(() => {
        return () => {
            window.clearTimeout(timeoutRef.current);
        };
    }, []);

    return (
        <ButtonBase
            fill_width={fill_width}
            ref={ref}
            type={type}
            onKeyDown={handleKeyDown}
            variant={variant}
            danger={danger}
            disabled={disabled}
            is_pressed={isPressed}
            id={id}
            align={align}
            padding_vertical={padding_vertical}
            padding_horizontal={icon ? "12" : padding_horizontal}
            onClick={onClick}
            size={size}
            onMouseDown={async () => {
                handleClick();
            }}
        >
            {align === "start" && icon && <IconContainer>{icon}</IconContainer>}

            {align === "center" && icon ? (
                <>
                    <Centered>
                        <HStack gap={"6"}>
                            <IconContainer>{icon}</IconContainer>
                            {children}
                        </HStack>
                    </Centered>
                </>
            ) : (
                children && (
                    <>
                        <HStack gap={"0"} fill_width={true} stick_to_sides={true}>
                            {size === "mini" || size === "slim" ? (
                                <BiggieSmall>{children}</BiggieSmall>
                            ) : (
                                <Text align={align} not_full_width={!!description}>
                                    {children}
                                </Text>
                            )}
                            {description && (
                                <>
                                    <Description>{description}</Description>
                                </>
                            )}
                        </HStack>
                    </>
                )
            )}
        </ButtonBase>
    );
};
